/**
 *  @class
 *  @function LocalizationForm
 */
function _debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('localization-form')) {
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.inputs = this.form
        ? this.form.querySelectorAll('select[name="locale_code"], select[name="country_code"]')
        : [];
      this.countrySelect = this.form
        ? this.form.querySelector('select[name="country_code"]')
        : null;
      this.countrySelector = this.querySelector('.select--ultra-minimal');
      this.countryTrigger = this.querySelector('.thb-country-selector-trigger');
      this.countryMenu = this.querySelector('.thb-country-selector-menu');
      this.countryOptions = Array.from(this.querySelectorAll('.thb-country-option'));

      this.handleDocumentClick = this.onDocumentClick.bind(this);
      this.handleWindowResize = this.onWindowResize.bind(this);
      this.debouncedOnSubmit = _debounce(() => {
        this.onSubmitHandler();
      }, 200);

      this.inputs.forEach((item) => {
        item.addEventListener('change', (event) => {
          this.updateCountryDisplay(event.currentTarget);
          this.debouncedOnSubmit();
        });
      });

      this.setupFlagFallbacks();
      this.applyCountrySelectorStyles();
      this.setupCountryDropdown();

      if (this.countrySelect) {
        this.updateCountryDisplay(this.countrySelect);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      window.removeEventListener('resize', this.handleWindowResize);
    }

    setupFlagFallbacks() {
      this.querySelectorAll('.thb-current-country-flag, .thb-country-option-flag').forEach((container) => {
        const image = container.querySelector('img');
        if (!image) return;

        image.addEventListener('load', () => {
          container.classList.remove('is-fallback');
        });

        image.addEventListener('error', () => {
          container.classList.add('is-fallback');
        });

        if (image.complete && image.naturalWidth === 0) {
          container.classList.add('is-fallback');
        }
      });
    }

    applyCountrySelectorStyles() {
      this.querySelectorAll('.thb-country-selector-arrow path').forEach((path) => {
        path.style.setProperty('stroke', '#fff', 'important');
      });
    }

    setupCountryDropdown() {
      if (!this.countrySelect || !this.countryTrigger || !this.countryMenu || !this.countrySelector) {
        return;
      }

      this.classList.add('is-enhanced');

      this.countryTrigger.addEventListener('click', () => {
        if (this.countryMenu.hidden) {
          this.openCountryMenu();
        } else {
          this.closeCountryMenu();
        }
      });

      this.countryTrigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.openCountryMenu('selected');
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.openCountryMenu('last');
        }
      });

      this.countryMenu.addEventListener('click', (event) => {
        const option = event.target.closest('.thb-country-option');
        if (!option) return;
        this.selectCountry(option.dataset.countryCode);
      });

      this.countryMenu.addEventListener('keydown', (event) => {
        this.onCountryMenuKeydown(event);
      });

      document.addEventListener('click', this.handleDocumentClick);
      window.addEventListener('resize', this.handleWindowResize);
    }

    updateCountryDisplay(select) {
      if (!select || select.name !== 'country_code') return;

      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) return;

      const countryCode = select.value.toUpperCase();
      const lowerCountryCode = countryCode.toLowerCase();
      const currencyCode = selectedOption.dataset.currencyCode || '';
      const countryName = selectedOption.dataset.countryName || selectedOption.textContent.trim();
      const flag = this.querySelector('.thb-current-country-flag');
      const flagImage = this.querySelector('.thb-country-flag-image');
      const currentCurrency = this.querySelector('.thb-current-country-code');

      if (flag && flagImage) {
        flag.dataset.countryCode = countryCode;
        flag.classList.remove('is-fallback');
        flagImage.src = `https://flagcdn.com/w40/${lowerCountryCode}.png`;
        flagImage.srcset = `https://flagcdn.com/w40/${lowerCountryCode}.png 1x, https://flagcdn.com/w80/${lowerCountryCode}.png 2x`;

        requestAnimationFrame(() => {
          if (flagImage.complete && flagImage.naturalWidth === 0) {
            flag.classList.add('is-fallback');
          }
        });
      }

      if (currentCurrency) {
        currentCurrency.textContent = currencyCode;
      }

      if (this.countryTrigger) {
        this.countryTrigger.setAttribute(
          'aria-label',
          `Change country or region. Current selection: ${countryName}, ${currencyCode}`
        );
      }

      this.countryOptions.forEach((option) => {
        const isSelected = option.dataset.countryCode === countryCode;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });
    }

    openCountryMenu(focusTarget = 'selected') {
      if (!this.countryMenu || !this.countryTrigger || !this.countrySelector) return;

      this.countryMenu.hidden = false;
      this.countryTrigger.setAttribute('aria-expanded', 'true');
      this.countrySelector.classList.add('is-open');

      requestAnimationFrame(() => {
        this.positionCountryMenu();

        const options = this.getCountryOptions();
        if (!options.length) return;

        let optionToFocus;
        if (focusTarget === 'last') {
          optionToFocus = options[options.length - 1];
        } else {
          optionToFocus = options.find((option) => option.classList.contains('is-selected')) || options[0];
        }

        optionToFocus.focus();
        optionToFocus.scrollIntoView({ block: 'nearest' });
      });
    }

    closeCountryMenu(returnFocus = false) {
      if (!this.countryMenu || !this.countryTrigger || !this.countrySelector) return;

      this.countryMenu.hidden = true;
      this.countryTrigger.setAttribute('aria-expanded', 'false');
      this.countrySelector.classList.remove('is-open');

      if (returnFocus) {
        this.countryTrigger.focus();
      }
    }

    selectCountry(countryCode) {
      if (!countryCode || !this.countrySelect) return;

      const hasChanged = this.countrySelect.value !== countryCode;
      this.countrySelect.value = countryCode;
      this.closeCountryMenu(true);

      if (hasChanged) {
        this.countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    getCountryOptions() {
      return this.countryOptions.filter((option) => !option.disabled && !option.hidden);
    }

    onCountryMenuKeydown(event) {
      const options = this.getCountryOptions();
      if (!options.length) return;

      const currentIndex = options.indexOf(document.activeElement);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          options[(currentIndex + 1 + options.length) % options.length].focus();
          break;
        case 'ArrowUp':
          event.preventDefault();
          options[(currentIndex - 1 + options.length) % options.length].focus();
          break;
        case 'Home':
          event.preventDefault();
          options[0].focus();
          break;
        case 'End':
          event.preventDefault();
          options[options.length - 1].focus();
          break;
        case 'Escape':
          event.preventDefault();
          this.closeCountryMenu(true);
          break;
        case 'Tab':
          this.closeCountryMenu();
          break;
        default:
          break;
      }
    }

    positionCountryMenu() {
      if (!this.countryMenu || this.countryMenu.hidden) return;

      this.countryMenu.classList.remove('opens-left');
      this.countryMenu.style.removeProperty('transform');
      this.countryMenu.style.removeProperty('max-width');

      const headerColumn = this.closest('.header')?.querySelector(':scope > .row > .columns');
      const fallbackGutter = window.innerWidth >= 768 ? 35 : 11;
      const headerBounds = headerColumn ? headerColumn.getBoundingClientRect() : null;
      const leftBoundary = headerBounds
        ? Math.max(fallbackGutter, headerBounds.left)
        : fallbackGutter;
      const rightBoundary = headerBounds
        ? Math.min(window.innerWidth - fallbackGutter, headerBounds.right)
        : window.innerWidth - fallbackGutter;
      const availableWidth = Math.max(0, rightBoundary - leftBoundary);

      this.countryMenu.style.maxWidth = `${availableWidth}px`;

      const menuRect = this.countryMenu.getBoundingClientRect();
      let shiftX = 0;

      if (menuRect.left < leftBoundary) {
        shiftX += leftBoundary - menuRect.left;
      }

      if (menuRect.right + shiftX > rightBoundary) {
        shiftX += rightBoundary - (menuRect.right + shiftX);
      }

      this.countryMenu.style.transform = `translateX(${Math.round(shiftX)}px)`;
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closeCountryMenu();
      }
    }

    onWindowResize() {
      if (this.countryMenu && !this.countryMenu.hidden) {
        this.positionCountryMenu();
      }
    }

    onSubmitHandler() {
      if (this.form) {
        this.form.submit();
      }
    }
  }

  customElements.define('localization-form', LocalizationForm);
}
